// VoiceSpectrum.Web.T2 · v10.0.10.10 · 2026-05-19 · VoiceSpectrum AI
//
// Button — primary / secondary / ghost / destructive with sm / md / lg sizes.
// Hierarchy is locked to docs/ui/finalized/ui-principles.html v2.2:
//   primary    — solid accent
//   secondary  — white + line border (default for non-CTA actions)
//   ghost      — transparent, low-emphasis
//   destructive — soft red surface (used inside modals + dangerous rows)

import { ButtonHTMLAttributes, forwardRef, ReactNode } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'destructive';
type Size = 'sm' | 'md' | 'lg';

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  block?: boolean;
  leading?: ReactNode;
  trailing?: ReactNode;
  loading?: boolean;
}

const variantClass: Record<Variant, string> = {
  primary:     'btn-primary',
  secondary:   'btn-secondary',
  ghost:       'btn-ghost',
  destructive: 'btn-danger',
};

const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    block = false,
    leading,
    trailing,
    loading = false,
    disabled,
    className,
    children,
    type = 'button',
    ...rest
  },
  ref,
) {
  const classes = [
    variantClass[variant],
    size === 'sm' ? 'btn-sm' : size === 'lg' ? 'btn-lg' : '',
    block ? 'btn-block' : '',
    className ?? '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <button ref={ref} type={type} className={classes} disabled={disabled || loading} {...rest}>
      {loading ? <span className="spinner" style={{ width: 14, height: 14, margin: 0, borderWidth: 2 }} /> : leading}
      {children}
      {!loading && trailing}
    </button>
  );
});

export default Button;
